import React from "react";
import { Dialog, Box, Typography, Button, IconButton, useMediaQuery, Chip, ToggleButtonGroup, ToggleButton, CircularProgress, Checkbox } from "@mui/material";
import { X, Check, Minus, ArrowLeft, CheckCircle2, Lock, Facebook, Linkedin, Instagram, User, CreditCard, ShoppingCart, ShieldCheck, Bell, ChevronLeft, ChevronRight, Plus } from "lucide-react";
import { Elements, PaymentElement } from "@stripe/react-stripe-js";
import theme from "../../util/theme/theme";
import { SlideUpTransition } from "./mobile/mobile-bottom-sheet";
import { useColorScheme } from "../../providers/color-scheme-provider";
import { useUser } from "../../providers/user-provider";
import { useSettings } from "../../providers/settings-provider";
import { useToast } from "../../providers/toaster-provider";
import useApi from "../../hooks/use-api";
import buildApiRequestConfig from "../../api/url-builder";
import routes from "../../api/routes";
import { syncSubscriptionAfterPayment } from '../../util/subscription-sync';
import axios from "axios";
import Cookies from "js-cookie";
import logoImage from "../../files/images/logo.png";
import logoDarkImage from "../../files/images/logo-dark.png";
import { TierConfig, BillingCycle, DEFAULT_TIERS, useSubscriptionTiers, planKeyOf, AddonKey, inclusionMode, offeredAddons, addonPrice, addonCharge, addonDescription } from "../../util/subscription-tiers";
import { usePlatformStripe, useConfirmStripePayment } from "../../util/platform-stripe";

export type { BillingCycle };
// Kept as an alias (rather than its own interface) so this file's plan data
// comes from one place - util/subscription-tiers.ts - shared with the admin
// "Subscription Tiers" page that edits it (see useSubscriptionTiers there).
export type SubscriptionPlan = TierConfig;

const CORE_FEATURES = [
    'Transactions',
    'Provisions',
    'Invoices',
    'Staff Payroll',
    'Summaries - P&L',
    'Summaries - Forecasts',
    'Summaries - Forecast vs Actual',
    'Categories (GL)',
    'Tax rates',
];

const ADVANCED_FEATURES = [
    'Configurable statuses',
    '>100 clients',
    '>10 employees',
    '>3 bank accounts',
    'Cash',
];

const TAGLINE_BY_PLAN: Record<string, string> = {
    Free: 'Get a feel for FineAnts with a 7-day trial.',
    Pro: 'Core cash flow tools for solo operators and small teams.',
    Elite: 'Full forecasting power with multi-user access and deeper integrations.',
};

const formatMoney = (n: number) => `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

// Mirrors the Checkout / Order Confirmation / Instant Access progression of
// the checkout flow this page's layout is modeled on - shown once a plan has
// been picked (not on the plan-picker itself, which is the step before this
// flow even starts).
const CHECKOUT_STEPS = ['Checkout', 'Order Confirmation', 'Instant Access'] as const;

const StepIndicator: React.FC<{ activeIndex: number }> = ({ activeIndex }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', mb: 5 }}>
        {CHECKOUT_STEPS.map((label, i) => {
            const state = i < activeIndex ? 'done' : i === activeIndex ? 'active' : 'upcoming';
            return (
                <React.Fragment key={label}>
                    {i > 0 && (
                        <Box sx={{ width: { xs: 24, sm: 56 }, height: 2, mt: 1.6, backgroundColor: i <= activeIndex ? 'var(--color-primary)' : 'var(--color-container-background)' }} />
                    )}
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.75, width: { xs: 56, sm: 90 } }}>
                        <Box
                            sx={{
                                width: 26,
                                height: 26,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                fontSize: 12,
                                fontWeight: 700,
                                backgroundColor: state === 'upcoming' ? 'var(--color-container-background)' : 'var(--color-primary)',
                                color: state === 'upcoming' ? 'var(--color-text-muted)' : '#fff',
                            }}
                        >
                            {state === 'done' ? <Check size={14} strokeWidth={3} /> : i + 1}
                        </Box>
                        <Typography
                            sx={{
                                fontSize: 12,
                                textAlign: 'center',
                                lineHeight: 1.3,
                                fontWeight: state === 'active' ? 700 : 500,
                                color: state === 'upcoming' ? 'var(--color-text-muted)' : 'var(--color-default-text)',
                            }}
                        >
                            {label}
                        </Typography>
                    </Box>
                </React.Fragment>
            );
        })}
    </Box>
);

const FeatureRow: React.FC<{ included: boolean; label: string; onDark?: boolean }> = ({ included, label, onDark }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.6 }}>
        <Box
            sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                backgroundColor: included
                    ? (onDark ? 'rgba(255,255,255,0.2)' : 'rgba(var(--color-primary-rgb), 0.12)')
                    : 'rgba(128,128,128,0.12)',
            }}
        >
            {included
                ? <Check size={13} strokeWidth={3} color={onDark ? '#fff' : 'var(--color-primary)'} />
                : <Minus size={12} strokeWidth={3} color="var(--color-text-muted)" />}
        </Box>
        <Typography sx={{ fontSize: 13.5, color: included ? (onDark ? '#fff' : 'var(--color-default-text)') : 'var(--color-text-muted)' }}>
            {label}
        </Typography>
    </Box>
);

// unavailableLabel: set in upgrade mode for the subscriber's current plan or
// anything below it - the card's button is disabled and shows this instead.
type AddonLine = { key: AddonKey; label: string; amount: number };

const PlanCard: React.FC<{ plan: SubscriptionPlan; comparePrice?: number; savingsPerMonth?: number; dark?: boolean; unavailableLabel?: string; onSelect?: (plan: SubscriptionPlan) => void }> = ({ plan, comparePrice, savingsPerMonth, dark, unavailableLabel, onSelect }) => {
    const onDark = !!plan.highlight || !!dark;
    return (
        <Box
            sx={{
                flex: '1 1 0',
                minWidth: 220,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 5,
                p: 3,
                pt: plan.highlight ? 4 : 3,
                background: plan.highlight
                    ? 'linear-gradient(160deg, var(--color-primary) 0%, var(--color-primary-hover) 100%)'
                    : dark
                        ? '#1c1f26'
                        : 'var(--color-container-background)',
                boxShadow: plan.highlight
                    ? '0 20px 40px -12px rgba(var(--color-primary-rgb), 0.45)'
                    : dark
                        ? '0 8px 24px rgba(0,0,0,0.25)'
                        : '0 2px 8px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.04)',
                transform: plan.highlight ? 'translateY(-8px)' : 'none',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': {
                    transform: plan.highlight ? 'translateY(-10px)' : 'translateY(-3px)',
                    boxShadow: plan.highlight
                        ? '0 24px 44px -10px rgba(var(--color-primary-rgb), 0.5)'
                        : dark
                            ? '0 12px 28px rgba(0,0,0,0.3)'
                            : '0 8px 20px rgba(0,0,0,0.08)',
                },
            }}
        >
            {plan.highlight && (
                <Chip
                    label="🔥 MOST POPULAR 🔥"
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: -13,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: '#fff',
                        color: 'var(--color-primary)',
                        fontWeight: 800,
                        fontSize: 11,
                        letterSpacing: 0.3,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                    }}
                />
            )}
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: onDark ? '#fff' : 'inherit' }}>{plan.name}</Typography>
            <Typography sx={{ fontSize: 12.5, color: onDark ? 'rgba(255,255,255,0.75)' : 'var(--color-text-muted)', mb: 2 }}>
                {plan.billingNote}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.5 }}>
                {!!comparePrice && comparePrice > plan.pricePerMonth && (
                    <Typography sx={{ fontSize: 16, fontWeight: 600, color: onDark ? 'rgba(255,255,255,0.55)' : 'var(--color-text-muted)', textDecoration: 'line-through' }}>
                        {formatMoney(comparePrice)}
                    </Typography>
                )}
                <Typography sx={{ fontSize: 38, fontWeight: 800, lineHeight: 1, color: onDark ? '#fff' : 'inherit' }}>
                    {formatMoney(plan.pricePerMonth)}
                </Typography>
                <Typography sx={{ fontSize: 13, color: onDark ? 'rgba(255,255,255,0.75)' : 'var(--color-text-muted)' }}>{plan.billing === 'daily' ? '/day' : '/month'}</Typography>
            </Box>
            {savingsPerMonth ? (
                <Typography sx={{ fontSize: 11.5, color: onDark ? '#fff' : '#1a7f37', fontWeight: 700, mb: 2 }}>
                    Save {formatMoney(savingsPerMonth)}/month vs. no-commitment
                </Typography>
            ) : (
                <Typography sx={{ fontSize: 11.5, color: onDark ? 'rgba(255,255,255,0.75)' : 'var(--color-text-muted)', mb: 2 }}>
                    {plan.minimumGuaranteed > 0
                        ? `${formatMoney(plan.minimumGuaranteed)} minimum guaranteed`
                        : 'No payment required'}
                </Typography>
            )}

            <Typography sx={{ fontSize: 13, lineHeight: 1.5, color: onDark ? 'rgba(255,255,255,0.85)' : 'var(--color-text-muted)', mb: 3, flex: 1 }}>
                {TAGLINE_BY_PLAN[plan.name] || 'Everything you need to forecast cash flow with confidence.'}
            </Typography>

            <Button
                variant="contained"
                fullWidth
                sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 14.5,
                    borderRadius: 999,
                    py: 1.1,
                    boxShadow: 'none',
                    backgroundColor: onDark ? '#fff' : 'var(--color-primary)',
                    color: onDark ? 'var(--color-primary)' : '#fff',
                    '&:hover': { backgroundColor: onDark ? '#fff' : 'var(--color-primary)', opacity: onDark ? 0.9 : 0.92, boxShadow: 'none' },
                    '&.Mui-disabled': {
                        backgroundColor: onDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.08)',
                        color: onDark ? 'rgba(255,255,255,0.85)' : 'var(--color-text-muted)',
                    },
                }}
                disabled={!!unavailableLabel}
                onClick={() => onSelect?.(plan)}
            >
                {unavailableLabel || 'Subscribe'}
            </Button>
        </Box>
    );
};

// One row per feature group that a given plan actually has - Kong's
// reference page never shows an excluded item, it just leaves the whole
// group out, so these are built per-plan rather than reusing one fixed list
// with included/excluded flags like the checkout sidebar's list does.
interface FeatureItem {
    label: string;
    detail: string;
}

interface FeatureGroup {
    heading: string;
    description: string;
    items: FeatureItem[];
}

// One short clarifying line per feature - kept to a few words, not a sales
// pitch, just enough that someone skimming the list knows what the label
// actually means in this app.
const FEATURE_DETAILS: Record<string, string> = {
    'User access control': "Decide what each teammate can see and edit.",
    'Xero integration': 'Transactions and invoices sync automatically.',
    'Stripe integration': 'Accept and track online payments.',
    'Documents': 'Share documents, invoices and reports with tracked links.',
    'Configurable statuses': 'Customize your invoice and payment stages.',
    '>100 clients': 'No cap on how many clients you manage.',
    '>10 employees': 'Run payroll for larger teams.',
    '>3 bank accounts': 'Connect more than the base 3 accounts.',
    'Cash': 'Track cash positions alongside forecasts.',
};

// includeAddons: list the plan's optional add-ons first (the plan cards) -
// left out at checkout, where they're picked with checkboxes instead.
const getFeatureGroups = (plan: SubscriptionPlan, { includeAddons = true }: { includeAddons?: boolean } = {}): FeatureGroup[] => {
    // Core isn't repeated per column - it's identical on every tier, already
    // said once in the page intro above ("every plan includes the full Core
    // feature set"), and listing all 9 items 3x over would just triple the
    // page's length without showing anything that actually differs.
    const addons = includeAddons ? offeredAddons(plan) : [];
    const groups: FeatureGroup[] = [
        ...(addons.length ? [{
            heading: 'Add-ons',
            description: 'Optional extras you can add at checkout.',
            items: addons.map(({ key, label, description }) => {
                const price = addonPrice(plan, key) ?? 0;
                return { label: `${label} ${price > 0 ? `+${formatMoney(price)}/mo` : '(free)'}`, detail: description };
            }),
        }] : []),
        {
            heading: 'Your Plan Features',
            description: 'How much room you get to work with.',
            items: [
                { label: `${plan.horizonDays}-day cash flow horizon`, detail: `See your cash position up to ${plan.horizonDays} days ahead.` },
                { label: `${plan.users} user${plan.users === 1 ? '' : 's'}`, detail: plan.users === 1 ? 'Just your own login.' : 'Invite your team to collaborate.' },
                ...(inclusionMode(plan, 'userAcl') === 'included' ? [{ label: 'User access control', detail: FEATURE_DETAILS['User access control'] }] : []),
                ...(inclusionMode(plan, 'documents') === 'included' ? [{ label: 'Documents', detail: FEATURE_DETAILS['Documents'] }] : []),
            ],
        },
    ];
    const integrations: FeatureItem[] = [
        ...(inclusionMode(plan, 'xeroIntegration') === 'included' ? [{ label: 'Xero integration', detail: FEATURE_DETAILS['Xero integration'] }] : []),
        ...(inclusionMode(plan, 'stripeIntegration') === 'included' ? [{ label: 'Stripe integration', detail: FEATURE_DETAILS['Stripe integration'] }] : []),
    ];
    if (integrations.length > 0) {
        groups.push({
            heading: 'Integrations',
            description: 'Connect the tools you already use.',
            items: integrations,
        });
    }
    if (inclusionMode(plan, 'advancedFeatures') === 'included') {
        groups.push({
            heading: 'Advanced Features',
            description: 'Built for teams managing more complexity.',
            items: ADVANCED_FEATURES.map(label => ({ label, detail: FEATURE_DETAILS[label] || '' })),
        });
    }
    return groups;
};

const FeatureItemRow: React.FC<{ item: FeatureItem }> = ({ item }) => (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, py: 0.6 }}>
        <Box
            sx={{
                width: 20,
                height: 20,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                mt: 0.1,
                backgroundColor: 'rgba(var(--color-primary-rgb), 0.12)',
            }}
        >
            <Check size={13} strokeWidth={3} color="var(--color-primary)" />
        </Box>
        <Box>
            <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{item.label}</Typography>
            {!!item.detail && (
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', lineHeight: 1.4 }}>
                    {item.detail}
                </Typography>
            )}
        </Box>
    </Box>
);

// The plan cards (row 1) and their breakdowns (row 2) as one horizontally
// scrolling grid, so a card and its breakdown always line up and move
// together. 3 per view on desktop, 1 (with a peek of the next) on mobile;
// with no more plans than fit, it looks like a plain row - no arrows.
// focusIndex: the card to centre in view when it opens (and whenever
// focusKey - the billing cycle - changes): the "Most popular" plan.
const PlanCarousel: React.FC<{ items: { key: string; card: React.ReactNode; features: React.ReactNode }[]; isMobile: boolean; focusIndex?: number; focusKey?: string }> = ({ items, isMobile, focusIndex = 0, focusKey }) => {
    const scrollerRef = React.useRef<HTMLDivElement>(null);
    const [canPrev, setCanPrev] = React.useState(false);
    const [canNext, setCanNext] = React.useState(false);
    const gapPx = isMobile ? 16 : 24;

    const updateArrows = React.useCallback(() => {
        const el = scrollerRef.current;
        if (!el) return;
        setCanPrev(el.scrollLeft > 4);
        setCanNext(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
    }, []);

    React.useEffect(() => {
        updateArrows();
        const el = scrollerRef.current;
        if (!el) return;
        el.addEventListener('scroll', updateArrows, { passive: true });
        window.addEventListener('resize', updateArrows);
        return () => {
            el.removeEventListener('scroll', updateArrows);
            window.removeEventListener('resize', updateArrows);
        };
    }, [updateArrows, items.length]);

    // Centre the focused card (clamped to the ends), without animating.
    React.useLayoutEffect(() => {
        const el = scrollerRef.current;
        const cell = el?.querySelectorAll<HTMLElement>('[data-plan-cell]')[focusIndex];
        if (!el || !cell) return;
        const target = cell.offsetLeft - (el.clientWidth - cell.offsetWidth) / 2;
        el.scrollLeft = Math.max(0, Math.min(target, el.scrollWidth - el.clientWidth));
        updateArrows();
    }, [focusIndex, focusKey, items.length, isMobile, updateArrows]);

    // One card at a time.
    const step = (direction: 1 | -1) => {
        const el = scrollerRef.current;
        const firstCard = el?.querySelector<HTMLElement>('[data-plan-cell]');
        if (!el || !firstCard) return;
        el.scrollBy({ left: direction * (firstCard.offsetWidth + gapPx), behavior: 'smooth' });
    };

    const arrow = (direction: 1 | -1, visible: boolean) => (
        <IconButton
            onClick={() => step(direction)}
            aria-label={direction === 1 ? 'Next plans' : 'Previous plans'}
            sx={{
                position: 'absolute',
                // Centred on the card row, not the whole grid.
                top: 200,
                [direction === 1 ? 'right' : 'left']: isMobile ? -4 : -22,
                zIndex: 2,
                width: 40,
                height: 40,
                backgroundColor: 'var(--color-container-background)',
                boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
                opacity: visible ? 1 : 0,
                pointerEvents: visible ? 'auto' : 'none',
                transition: 'opacity 0.15s ease',
                '&:hover': { backgroundColor: 'var(--color-container-background)' },
            }}
        >
            {direction === 1 ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
        </IconButton>
    );

    return (
        <Box sx={{ position: 'relative', mb: 1 }}>
            {arrow(-1, canPrev)}
            {arrow(1, canNext)}
            <Box
                ref={scrollerRef}
                sx={{
                    // Positioned so the cards' offsetLeft is measured from here
                    // (used to centre the focused card).
                    position: 'relative',
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': { display: 'none' },
                    // Room for the "Most popular" chip and the cards' hover lift,
                    // which would otherwise be clipped by the scroll container.
                    pt: 3,
                    pb: 2,
                    mx: -1,
                    px: 1,
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridAutoFlow: 'column',
                        gridTemplateRows: 'auto auto',
                        gridAutoColumns: isMobile ? '85%' : `calc((100% - ${gapPx * 2}px) / 3)`,
                        columnGap: `${gapPx}px`,
                        rowGap: 5,
                    }}
                >
                    {items.map((item, index) => (
                        <Box key={`${item.key}-card`} data-plan-cell sx={{ gridRow: 1, gridColumn: index + 1, display: 'flex', scrollSnapAlign: 'start' }}>
                            {item.card}
                        </Box>
                    ))}
                    {items.map((item, index) => (
                        <Box key={`${item.key}-features`} sx={{ gridRow: 2, gridColumn: index + 1, display: 'flex' }}>
                            {item.features}
                        </Box>
                    ))}
                </Box>
            </Box>
        </Box>
    );
};

const PlanFeatureColumn: React.FC<{ plan: SubscriptionPlan }> = ({ plan }) => (
    <Box sx={{ flex: '1 1 0', minWidth: 220 }}>
        {getFeatureGroups(plan).map(group => (
            <Box key={group.heading} sx={{ mb: 3.5 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, mb: 0.5 }}>{group.heading}</Typography>
                <Typography sx={{ fontSize: 12.5, color: 'var(--color-text-muted)', lineHeight: 1.5, mb: 1.5 }}>
                    {group.description}
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {group.items.map(item => <FeatureItemRow key={item.label} item={item} />)}
                </Box>
            </Box>
        ))}
    </Box>
);

type Step = 'plans' | 'checkout' | 'success' | 'access';

// Site footer under the pricing page, modeled on the reference's layout
// (logo / social icons / copyright / links) but with no background band of
// its own - it just sits on the page's own background like everything else
// here, so it uses the same light/dark-aware logo swap as the header above
// instead of being forced into a separate dark moment. Support links to the
// real support mailbox (web.config's SUPPORT_EMAIL) and Terms to the real
// /terms-and-conditions route; there's no dedicated Privacy Policy page yet
// so that one points at the same Terms page rather than a dead link.
const PlansFooter: React.FC<{ isDark: boolean }> = ({ isDark }) => (
    // mt: 'auto' (rather than a fixed value) pushes this to the bottom of the
    // flex column it lives in - when the page content is shorter than the
    // viewport, this fills the rest instead of leaving blank page background
    // under it; when content already overflows, it just sits right after it.
    // A direct sibling of the padded content box above (not nested inside
    // it), so it needs no margin trick of its own to reach full width.
    <Box sx={{ mt: 'auto', pt: 6, px: 3, pb: 4, backgroundColor: 'var(--color-container-background)', textAlign: 'center', flexShrink: 0 }}>
        <img src={isDark ? logoDarkImage : logoImage} alt="FineAnts" style={{ width: 128, height: 32 }} />
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1.5, mt: 2.5 }}>
            {[Facebook, Linkedin, Instagram].map((Icon, i) => (
                <Box
                    key={i}
                    sx={{
                        width: 32,
                        height: 32,
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Icon size={16} color="var(--color-primary)" />
                </Box>
            ))}
        </Box>
        <Typography sx={{ fontSize: 12.5, color: 'var(--color-text-muted)', mt: 3 }}>
            Copyright © FineAnts {new Date().getFullYear()}. All Rights Reserved.
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, mt: 1 }}>
            <a href="mailto:support@fineants.com" style={{ fontSize: 12.5, color: 'var(--color-text-muted)', textDecoration: 'none' }}>Support</a>
            <Typography sx={{ fontSize: 12.5, color: 'var(--color-text-muted)', opacity: 0.4 }}>|</Typography>
            <a href="/terms-and-conditions" target="_blank" rel="noreferrer" style={{ fontSize: 12.5, color: 'var(--color-text-muted)', textDecoration: 'none' }}>Terms Of Service</a>
            <Typography sx={{ fontSize: 12.5, color: 'var(--color-text-muted)', opacity: 0.4 }}>|</Typography>
            <a href="/terms-and-conditions" target="_blank" rel="noreferrer" style={{ fontSize: 12.5, color: 'var(--color-text-muted)', textDecoration: 'none' }}>Privacy Policy</a>
        </Box>
    </Box>
);

// Read-only - the checkout flow only ever runs for an already-logged-in
// user with an existing entity, not a fresh signup, so this is real account
// data (useUser/useSettings), not something to ask them to re-type.
const CheckoutDetailRow: React.FC<{ label: string; value?: string | null }> = ({ label, value }) => (
    <Box>
        <Typography sx={{ fontSize: 12.5, fontWeight: 600, mb: 0.5 }}>{label}</Typography>
        <Typography sx={{ fontSize: 14, color: value ? 'inherit' : 'var(--color-text-muted)' }}>{value || '—'}</Typography>
    </Box>
);

const KongSectionHeader: React.FC<{ icon: React.ReactNode; children: React.ReactNode }> = ({ icon, children }) => (
    <Box sx={{ mt: 3.5, mb: 2, '&:first-of-type': { mt: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            {icon}
            <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{children}</Typography>
        </Box>
        <Box sx={{ borderBottom: '1px solid rgba(0,0,0,0.1)' }} />
    </Box>
);

// Two-column "real checkout page" layout (billing/payment form on the left,
// a sticky order summary + the actual CTA on the right) rather than a small
// centered card - modeled on the reference checkout page. "Your Details"
// is read-only (useUser/useSettings) rather than editable fields, since
// this only ever runs for an already-logged-in user with an existing
// entity, not a fresh signup - only the Billing Information section is
// real, backed by Stripe Elements for a paid plan (see billingFieldsSlot).
const CheckoutView: React.FC<{
    plan: SubscriptionPlan;
    billing: BillingCycle;
    processing: boolean;
    // Real Stripe Elements UI for a paid plan (<PaymentElement/>, rendered
    // by a wrapper inside <Elements> - see PaidCheckoutStep below), or null
    // for the Free plan, which never touches Stripe at all.
    billingFieldsSlot?: React.ReactNode;
    errorMessage?: string;
    // Add-ons ticked below - each charged for this first billing period with
    // this payment, on top of the plan.
    addonLines?: AddonLine[];
    // The plan's optional add-ons, picked here (unticked by default).
    // addonsLocked once payment has started (its amount is set) - unlocked
    // again with onChangeAddons. onContinueToPayment: payment hasn't started
    // yet, so the main button continues to it with the add-ons chosen.
    addonOptions?: { key: AddonKey; label: string; description: string; amount: number }[];
    selectedAddonKeys?: AddonKey[];
    onToggleAddon?: (key: AddonKey, checked: boolean) => void;
    addonsLocked?: boolean;
    onChangeAddons?: () => void;
    onContinueToPayment?: () => void;
    onBack: () => void;
    onConfirm: () => void;
}> = ({ plan, billing, processing, billingFieldsSlot, errorMessage, addonLines = [], addonOptions = [], selectedAddonKeys = [], onToggleAddon, addonsLocked = false, onChangeAddons, onContinueToPayment, onBack, onConfirm }) => {
    const isFree = plan.pricePerMonth === 0;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { user } = useUser();
    const { companySettings } = useSettings();
    // plan.minimumGuaranteed already IS the actual amount charged right now in
    // either case - one month's price for a no-commitment plan, the full
    // 12-month commitment for an up-front plan - so it's the one number to
    // show and charge, never pricePerMonth alone when billing is annual. Plus
    // any ticked add-ons.
    const addonsTotal = addonLines.reduce((sum, line) => sum + line.amount, 0);
    const amountDueNow = plan.minimumGuaranteed + addonsTotal;

    const confirmLabel = processing
        ? null
        : onContinueToPayment
            ? `Continue to payment (${formatMoney(amountDueNow)})`
            : isFree
                ? 'Start Free Plan'
                : billing === 'annual' ? `Pay ${formatMoney(amountDueNow)} now` : `Subscribe for ${formatMoney(amountDueNow)}/${billing === 'daily' ? 'day' : 'month'}`;

    return (
        <Box sx={{ pt: 1, maxWidth: 820, mx: 'auto' }}>
            <StepIndicator activeIndex={0} />

            <Button
                startIcon={<ArrowLeft size={16} />}
                onClick={onBack}
                disabled={processing}
                sx={{ textTransform: 'none', fontWeight: 600, color: 'var(--color-text-muted)', mb: 2, pl: 0, '&:hover': { backgroundColor: 'transparent' } }}
            >
                Back to plans
            </Button>

            {/* Roughly equal-width columns (not form-heavy), matching the
                reference rather than a wide form beside a narrow sidebar. */}
            <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 4 : 5, alignItems: 'flex-start' }}>
                {/* LEFT: billing + payment form */}
                <Box sx={{ flex: '1 1 0', minWidth: 0, width: '100%', order: isMobile ? 2 : 1 }}>
                    <KongSectionHeader icon={<User size={15} />}>Your Details</KongSectionHeader>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                        <CheckoutDetailRow label="Full name" value={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()} />
                        <CheckoutDetailRow label="Email address" value={user?.email} />
                        <CheckoutDetailRow label="Company" value={companySettings?.company?.name} />
                    </Box>

                    {addonOptions.length > 0 && (
                        <>
                            <KongSectionHeader icon={<Plus size={15} />}>Add-ons</KongSectionHeader>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {addonOptions.map(addon => {
                                    const checked = selectedAddonKeys.includes(addon.key);
                                    return (
                                        <Box
                                            key={addon.key}
                                            component="label"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.25,
                                                p: 1.25,
                                                borderRadius: 2,
                                                border: '1px solid',
                                                borderColor: checked ? 'var(--color-primary)' : 'rgba(0,0,0,0.12)',
                                                background: checked ? 'rgba(var(--color-primary-rgb), 0.06)' : 'transparent',
                                                cursor: addonsLocked ? 'default' : 'pointer',
                                                opacity: addonsLocked && !checked ? 0.6 : 1,
                                            }}
                                        >
                                            <Checkbox
                                                size="small"
                                                checked={checked}
                                                disabled={addonsLocked}
                                                onChange={(e) => onToggleAddon?.(addon.key, e.target.checked)}
                                                sx={{ p: 0, '&.Mui-checked': { color: 'var(--color-primary)' } }}
                                            />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{addon.label}</Typography>
                                                <Typography sx={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>{addon.description}</Typography>
                                            </Box>
                                            <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'var(--color-primary)', whiteSpace: 'nowrap' }}>
                                                {addon.amount > 0 ? `+${formatMoney(addon.amount)}` : 'Free'}
                                                {addon.amount > 0 && (
                                                    <Box component="span" sx={{ fontSize: 11, fontWeight: 500, opacity: 0.8 }}>
                                                        {billing === 'annual' ? ' / 12 months' : billing === 'daily' ? '/day' : '/mo'}
                                                    </Box>
                                                )}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                            </Box>
                            {addonsLocked && onChangeAddons && !processing && (
                                <Button onClick={onChangeAddons} sx={{ textTransform: 'none', fontSize: 12.5, mt: 0.5, pl: 0, color: 'var(--color-primary)' }}>
                                    Change add-ons
                                </Button>
                            )}
                        </>
                    )}

                    {!isFree && (
                        <>
                            <KongSectionHeader icon={<CreditCard size={15} />}>Billing Information</KongSectionHeader>
                            {billingFieldsSlot ?? (onContinueToPayment ? (
                                <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)', py: 1 }}>
                                    {addonOptions.length ? 'Choose any add-ons above, then continue to payment.' : 'Continue to payment to enter your card details.'}
                                </Typography>
                            ) : errorMessage ? (
                                <Typography sx={{ fontSize: 12.5, color: '#e53935' }}>{errorMessage}</Typography>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
                                    <CircularProgress size={18} sx={{ color: 'var(--color-primary)' }} />
                                    <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Preparing secure checkout…</Typography>
                                </Box>
                            ))}
                            {billingFieldsSlot && errorMessage && (
                                <Typography sx={{ fontSize: 12.5, color: '#e53935', mt: 1.5 }}>{errorMessage}</Typography>
                            )}
                            <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)', mt: 1.5 }}>
                                By providing your card information, you allow FineAnts to charge your card for future payments in accordance with their terms.
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Lock size={12} color="var(--color-text-muted)" />
                                    <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>SSL Encryption</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <ShieldCheck size={12} color="var(--color-text-muted)" />
                                    <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Privacy Protected</Typography>
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>

                {/* RIGHT: order summary sits directly on the page (no card
                    background of its own) - only the price + CTA at the very
                    bottom is boxed, matching the reference exactly. */}
                <Box sx={{ width: isMobile ? '100%' : 340, flexShrink: 0, order: isMobile ? 1 : 2, position: isMobile ? 'static' : 'sticky', top: 16 }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 800 }}>
                        {plan.name} Plan
                    </Typography>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.4, mt: 1, mb: 3 }}>
                        {TAGLINE_BY_PLAN[plan.name] || 'Everything you need to forecast cash flow with confidence.'}
                    </Typography>

                    <Typography sx={{ fontSize: 15, fontWeight: 800, mb: 2 }}>
                        Here's What You're Getting
                    </Typography>
                    {getFeatureGroups(plan, { includeAddons: false }).map(group => (
                        <Box key={group.heading} sx={{ mb: 2.5 }}>
                            <Typography sx={{ fontSize: 13.5, fontWeight: 800, mb: 0.25 }}>{group.heading}</Typography>
                            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)', lineHeight: 1.4, mb: 1 }}>
                                {group.description}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                {group.items.map(item => <FeatureItemRow key={item.label} item={item} />)}
                            </Box>
                        </Box>
                    ))}

                    <Box
                        sx={{
                            borderRadius: 3,
                            border: '1px solid rgba(0,0,0,0.12)',
                            p: 2.5,
                            mt: 3,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                            <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>{formatMoney(amountDueNow)}</Typography>
                            <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>
                                {billing === 'annual' ? '/ 12 months' : billing === 'daily' ? '/day' : '/month'}
                            </Typography>
                        </Box>
                        {billing === 'annual' && !isFree && (
                            <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)', mt: 0.25, mb: 1.5 }}>
                                Billed today, up front*
                            </Typography>
                        )}
                        {addonsTotal > 0 && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: billing === 'annual' ? 0 : 1, mb: 1.5, fontSize: 12.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <span>{plan.name} plan</span>
                                    <span>{formatMoney(plan.minimumGuaranteed)}</span>
                                </Box>
                                {addonLines.filter(line => line.amount > 0).map(line => (
                                    <Box key={line.key} sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: 'var(--color-primary)' }}>
                                        <span>+ {line.label}{billing === 'annual' ? ' (12 months)' : ''}</span>
                                        <span>{formatMoney(line.amount)}</span>
                                    </Box>
                                ))}
                            </Box>
                        )}

                        <Button
                            variant="contained"
                            fullWidth
                            disabled={processing || (!isFree && !billingFieldsSlot && !onContinueToPayment)}
                            startIcon={processing ? undefined : <ShoppingCart size={16} />}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 700,
                                fontSize: 15,
                                borderRadius: 999,
                                py: 1.3,
                                mt: 2,
                                boxShadow: 'none',
                                backgroundColor: 'var(--color-primary)',
                                color: '#fff',
                                '&:hover': { backgroundColor: 'var(--color-primary)', opacity: 0.92, boxShadow: 'none' },
                            }}
                            onClick={onContinueToPayment || onConfirm}
                        >
                            {processing ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : confirmLabel}
                        </Button>
                        <Typography sx={{ fontSize: 10.5, color: 'var(--color-text-muted)', textAlign: 'center', mt: 1.5 }}>
                            * Offer subject to terms of use.
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

// Wraps CheckoutView for a PAID plan - must be rendered inside <Elements>
// (useStripe/useElements throw otherwise), which is why this isn't just a
// branch inside CheckoutView itself. The Free plan never mounts this at
// all, so it never needs a live Stripe context.
const PaidCheckoutStep: React.FC<{
    plan: SubscriptionPlan;
    billing: BillingCycle;
    onBack: () => void;
    onPaid: () => void;
    addonLines?: AddonLine[];
    addonOptions?: { key: AddonKey; label: string; description: string; amount: number }[];
    selectedAddonKeys?: AddonKey[];
    onChangeAddons?: () => void;
}> = ({ plan, billing, onBack, onPaid, addonLines, addonOptions, selectedAddonKeys, onChangeAddons }) => {
    const { confirm, processing, error } = useConfirmStripePayment(onPaid);

    return (
        <CheckoutView
            plan={plan}
            billing={billing}
            processing={processing}
            billingFieldsSlot={<PaymentElement />}
            errorMessage={error}
            addonLines={addonLines}
            addonOptions={addonOptions}
            selectedAddonKeys={selectedAddonKeys}
            addonsLocked
            onChangeAddons={onChangeAddons}
            onBack={onBack}
            onConfirm={confirm}
        />
    );
};

export interface Receipt {
    id: string;
    date: string;
    plan: SubscriptionPlan;
    billing: BillingCycle;
    amountCharged: number;
    last4: string | null;
    // null for the Free plan - it has no recurring charge, so there's
    // nothing to auto-renew or warn about.
    nextBillingDate: string | null;
    // Set only when this was a plan CHANGE for an already-paying subscriber
    // (changePlan bills the full new amount immediately, no auto-proration)
    // - the unused portion of their previous plan, for the manual
    // reimbursement process to use as a starting point. Never auto-applied.
    suggestedRefund?: number;
    // Add-ons picked at checkout - each its own receipt line after the plan.
    addonLines: AddonLine[];
}

const ReceiptCard: React.FC<{ receipt: Receipt }> = ({ receipt }) => {
    const { plan, billing, amountCharged, last4 } = receipt;
    const lineLabel = billing === 'annual' ? `${plan.name} plan - 12 months up front` : `${plan.name} plan - ${billing === 'daily' ? 'daily' : 'monthly'}`;
    const addonLines = receipt.addonLines.filter(line => line.amount > 0);
    return (
        <Box
            sx={{
                borderRadius: 4,
                backgroundColor: 'var(--color-container-background)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.04)',
                p: 3,
                mb: 4,
                textAlign: 'left',
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--color-text-muted)' }}>
                    Receipt
                </Typography>
                <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{receipt.date}</Typography>
            </Box>
            <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)', mb: 2.5 }}>
                Receipt No. {receipt.id}
            </Typography>

            <Box sx={{ borderTop: '1px dashed rgba(0,0,0,0.15)', borderBottom: '1px dashed rgba(0,0,0,0.15)', py: 1.5, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography sx={{ fontSize: 13.5 }}>{lineLabel}</Typography>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {formatMoney(plan.minimumGuaranteed)}
                    </Typography>
                </Box>
                {addonLines.map(line => (
                    <Box key={line.key} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                        <Typography sx={{ fontSize: 13.5 }}>
                            {line.label} add-on{billing === 'annual' ? ' - 12 months up front' : billing === 'daily' ? ' - daily' : ' - monthly'}
                        </Typography>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                            {formatMoney(line.amount)}
                        </Typography>
                    </Box>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                    <Typography sx={{ fontSize: 13.5, color: 'var(--color-text-muted)' }}>
                        {last4 ? `Card ending in ${last4}` : amountCharged > 0 ? 'Paid via card on file' : 'No payment method required'}
                    </Typography>
                </Box>
                {receipt.nextBillingDate && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                        <Typography sx={{ fontSize: 13.5, color: 'var(--color-text-muted)' }}>
                            Next payment
                        </Typography>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>
                            {receipt.nextBillingDate}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 15, fontWeight: 800 }}>Total paid</Typography>
                <Typography sx={{ fontSize: 15, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>
                    {formatMoney(amountCharged)}
                </Typography>
            </Box>
        </Box>
    );
};

const SuccessView: React.FC<{ receipt: Receipt; onContinue: () => void }> = ({ receipt, onContinue }) => (
    <Box sx={{ maxWidth: 440, mx: 'auto', pt: 1, textAlign: 'center' }}>
        <StepIndicator activeIndex={1} />
        <Box
            sx={{
                width: 72,
                height: 72,
                borderRadius: '50%',
                backgroundColor: 'rgba(26, 127, 55, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 3,
            }}
        >
            <CheckCircle2 size={38} color="#1a7f37" />
        </Box>
        <Typography sx={{ fontSize: 24, fontWeight: 800, mb: 1 }}>You're subscribed!</Typography>
        <Typography sx={{ fontSize: 14, color: 'var(--color-text-muted)', mb: 4 }}>
            You're all set on the {receipt.plan.name} plan{receipt.plan.pricePerMonth > 0 ? ` at ${formatMoney(receipt.plan.pricePerMonth)}/month` : ''}. You can change or cancel your plan anytime.
        </Typography>

        <ReceiptCard receipt={receipt} />

        {receipt.nextBillingDate && (
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25,
                    textAlign: 'left',
                    borderRadius: 3,
                    backgroundColor: 'var(--color-container-background)',
                    p: 2,
                    mb: 3,
                }}
            >
                <Bell size={16} style={{ flexShrink: 0, marginTop: 2 }} color="var(--color-text-muted)" />
                <Typography sx={{ fontSize: 12.5, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
                    Your {receipt.plan.name} plan auto-renews on <strong style={{ color: 'var(--color-default-text)' }}>{receipt.nextBillingDate}</strong>, charging the same card automatically. To stop it from renewing, turn off auto-renew under Billing Settings before that date.
                </Typography>
            </Box>
        )}

        {/* Only present for a plan CHANGE, not a fresh subscribe - the full
            new amount was just charged with no auto-proration, so this is
            the unused portion of the PREVIOUS plan, credited back as a
            separate manual step, never automatically. */}
        {!!receipt.suggestedRefund && receipt.suggestedRefund > 0 && (
            <Box sx={{ textAlign: 'left', borderRadius: 3, backgroundColor: 'var(--color-container-background)', p: 2, mb: 3 }}>
                <Typography sx={{ fontSize: 12.5, color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
                    The unused portion of your previous plan will be credited back separately.
                </Typography>
            </Box>
        )}

        <Button
            variant="contained"
            fullWidth
            sx={{
                textTransform: 'none',
                fontWeight: 700,
                fontSize: 15,
                borderRadius: 999,
                py: 1.3,
                boxShadow: 'none',
                backgroundColor: 'var(--color-primary)',
                color: '#fff',
                '&:hover': { backgroundColor: 'var(--color-primary)', opacity: 0.92, boxShadow: 'none' },
            }}
            onClick={onContinue}
        >
            Continue
        </Button>
    </Box>
);

// The reference's actual 3rd step - a real screen, not just a relabeled
// close button on step 2. Content is necessarily generic (this component
// has no account/entity context to personalize with), but it's a genuine
// step: a welcome moment plus a short, plan-aware checklist of what to do
// next, before the final action actually leaves the flow.
const AccessView: React.FC<{ plan: SubscriptionPlan; onGoToDashboard: () => void }> = ({ plan, onGoToDashboard }) => {
    const nextSteps = [
        'Explore your dashboard and existing data',
        ...(inclusionMode(plan, 'xeroIntegration') === 'included' ? ['Connect Xero to sync invoices and transactions automatically'] : []),
        ...(plan.users > 1 ? ['Invite your team'] : []),
    ];
    return (
        <Box sx={{ maxWidth: 440, mx: 'auto', pt: 1, textAlign: 'center' }}>
            <StepIndicator activeIndex={2} />
            <Box
                sx={{
                    width: 72,
                    height: 72,
                    borderRadius: '50%',
                    background: 'linear-gradient(160deg, var(--color-primary) 0%, var(--color-primary-hover) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                }}
            >
                <Check size={36} strokeWidth={3} color="#fff" />
            </Box>
            <Typography sx={{ fontSize: 24, fontWeight: 800, mb: 1 }}>You're in!</Typography>
            <Typography sx={{ fontSize: 14, color: 'var(--color-text-muted)', mb: 4 }}>
                Your {plan.name} plan is active right now - nothing else to set up before you start using it.
            </Typography>

            <Box
                sx={{
                    borderRadius: 4,
                    backgroundColor: 'var(--color-container-background)',
                    p: 3,
                    mb: 4,
                    textAlign: 'left',
                }}
            >
                <Typography sx={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--color-text-muted)', mb: 1.5 }}>
                    What's next
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    {nextSteps.map(step => <FeatureRow key={step} included label={step} />)}
                </Box>
            </Box>

            <Button
                variant="contained"
                fullWidth
                sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 15,
                    borderRadius: 999,
                    py: 1.3,
                    boxShadow: 'none',
                    backgroundColor: 'var(--color-primary)',
                    color: '#fff',
                    '&:hover': { backgroundColor: 'var(--color-primary)', opacity: 0.92, boxShadow: 'none' },
                }}
                onClick={onGoToDashboard}
            >
                Go to Dashboard
            </Button>
        </Box>
    );
};

export interface SubscriptionPlansProps {
    open: boolean;
    onClose: () => void;
    onSelect?: (plan: SubscriptionPlan) => void;
    // Upgrade mode: the subscriber's current tier. Only plans with a longer
    // horizon than it can be picked - the current plan and anything below it
    // are shown but locked. Omitted = every plan is selectable.
    upgradeFromTierId?: string;
    // Manage mode (Company Settings > Subscription "Manage"): the current
    // tier is marked "Current plan"; every other plan - up or down - can be
    // picked.
    currentTierId?: string;
}

const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({ open, onClose, onSelect, upgradeFromTierId, currentTierId }) => {
    const { showSuccess } = useToast();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { isDark } = useColorScheme();
    const { user } = useUser();
    const [billing, setBilling] = React.useState<BillingCycle>('monthly');
    const [step, setStep] = React.useState<Step>('plans');
    const [selectedPlan, setSelectedPlan] = React.useState<SubscriptionPlan | null>(null);
    const [processing, setProcessing] = React.useState<boolean>(false);
    const [receipt, setReceipt] = React.useState<Receipt | null>(null);
    const { tiers, refetch: refetchTiers } = useSubscriptionTiers();
    // Add-ons are picked at checkout (unticked by default). Payment for a plan
    // that offers add-ons only starts once the user continues past that
    // choice - starting it sets the amount (and, for a company that already
    // pays, charges it straight away), so the add-ons have to be known first.
    const [checkoutAddons, setCheckoutAddons] = React.useState<AddonKey[]>([]);
    const [paymentStarted, setPaymentStarted] = React.useState(false);
    const addonOptionsFor = (plan: SubscriptionPlan | null) => (plan ? offeredAddons(plan).map(a => ({ key: a.key, label: a.label, description: a.description, amount: addonCharge(plan, a.key) })) : []);
    const addonsFor = (plan: SubscriptionPlan | null): AddonKey[] => {
        if (!plan) return [];
        const offered = offeredAddons(plan).map(a => a.key);
        return checkoutAddons.filter(k => offered.includes(k));
    };
    const addonLinesFor = (plan: SubscriptionPlan | null): AddonLine[] => (
        addonsFor(plan).map(key => ({ key, label: offeredAddons(plan).find(a => a.key === key)?.label || key, amount: addonCharge(plan, key) }))
    );
    const addonPriceFor = (plan: SubscriptionPlan | null) => addonLinesFor(plan).reduce((sum, line) => sum + line.amount, 0);
    const toggleCheckoutAddon = (key: AddonKey, checked: boolean) => setCheckoutAddons(prev => (
        checked ? [...prev.filter(k => k !== key), key] : prev.filter(k => k !== key)
    ));

    // Real Stripe billing for a paid plan - populated as soon as a paid plan
    // is picked, so <Elements> has a clientSecret to mount PaymentElement
    // against (see PaidCheckoutStep). The Free plan never touches any of
    // this. Always goes through stripeUpgradeForFeature (not stripeInitiate
    // directly) - upgradeForFeature already branches server-side between a
    // fresh subscription (mode: 'initiate', a Free/new subscriber) and
    // changing an EXISTING one (mode: 'change', an already-paying
    // subscriber picking a different paid tier) - routing a plan change
    // through stripeInitiate instead would have created a second, parallel
    // Stripe subscription rather than replacing the first.
    const [initiateResult, setInitiate] = useApi<{ mode: 'initiate' | 'change'; clientSecret: string | null; suggestedRefund?: number }>();
    const [selectResult, setSelect] = useApi<{ success: boolean }>();
    const { stripePromise, error: stripeKeyError } = usePlatformStripe();
    const stripeClientSecret = initiateResult.data?.clientSecret ?? null;
    // The server's own message (errorData.error) when it sent one - not the
    // generic "Request failed with status code 400".
    const stripeInitError = initiateResult.error
        ? (initiateResult.errorData?.error || (typeof initiateResult.error === 'string' ? initiateResult.error : 'Failed to start checkout. Please try again.'))
        : '';
    // mode: 'change' with no clientSecret means changePlan already charged
    // the entity's existing saved payment method in full - nothing left to
    // confirm, same as upgrade-required-dialog.tsx's identical case.
    const alreadyCharged = !!initiateResult.data && initiateResult.data.mode === 'change' && !initiateResult.data.clientSecret;

    // Fresh start every time this is reopened - picking up mid-checkout from
    // a previous visit would be confusing, not convenient. Also re-fetches
    // tiers so an edit made in the admin "Subscription Tiers" page shows up
    // the next time this is opened, without needing a full reload.
    React.useEffect(() => {
        if (open) {
            setStep('plans');
            setSelectedPlan(null);
            setProcessing(false);
            setReceipt(null);
            setInitiate(null);
            setCheckoutAddons([]);
            setPaymentStarted(false);
            refetchTiers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const buildReceipt = (plan: SubscriptionPlan, last4: string | null, suggestedRefund?: number): Receipt => {
        // Free has no billing cycle (plan.billing is null) - nothing ever
        // gets charged again, so there's no next-payment date to show.
        let nextBillingDate: string | null = null;
        if (plan.billing) {
            const next = new Date();
            if (plan.billing === 'annual') {
                next.setFullYear(next.getFullYear() + 1);
            } else if (plan.billing === 'daily') {
                next.setDate(next.getDate() + 1);
            } else {
                next.setMonth(next.getMonth() + 1);
            }
            nextBillingDate = next.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
        }
        return {
            id: `FA-${Date.now().toString().slice(-8)}`,
            date: new Date().toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }),
            plan,
            billing,
            // minimumGuaranteed is already the real amount charged right now
            // - one month's price for no-commitment, the full 12-month
            // commitment for an up-front plan - never pricePerMonth alone
            // once billing is annual.
            amountCharged: plan.minimumGuaranteed + addonPriceFor(plan),
            last4,
            nextBillingDate,
            suggestedRefund,
            addonLines: addonLinesFor(plan),
        };
    };

    // Starts the real payment for a paid plan with the add-ons chosen.
    const startPayment = (plan: SubscriptionPlan, addons: AddonKey[]) => {
        if (plan.pricePerMonth <= 0 || !user?.entityId) return;
        setPaymentStarted(true);
        const { url, method } = buildApiRequestConfig(routes.subscription.stripeUpgradeForFeature, [
            { param: 'entityId', value: user.entityId }
        ]);
        setInitiate({ url, method, body: { tierId: plan.id, addons } });
    };

    const handlePickPlan = (plan: SubscriptionPlan) => {
        setSelectedPlan(plan);
        setStep('checkout');
        setCheckoutAddons([]);
        setPaymentStarted(false);
        setInitiate(null);
        // A plan with add-ons waits on the checkout's "Continue to payment"
        // so they can be ticked first; one without starts right away.
        if (offeredAddons(plan).length > 0) return;
        if (plan.pricePerMonth > 0 && user?.entityId) {
            // For a Free/new subscriber this kicks off a real Stripe
            // subscription in `default_incomplete` state and gets back a
            // PaymentIntent client_secret - nothing is charged yet, and
            // nothing changes on the entity's plan until the card is
            // actually confirmed (see PaidCheckoutStep). For an
            // already-paying subscriber picking a different paid tier, this
            // instead bills the full new amount immediately against their
            // existing saved payment method (see alreadyCharged below).
            const { url, method } = buildApiRequestConfig(routes.subscription.stripeUpgradeForFeature, [
                { param: 'entityId', value: user.entityId }
            ]);
            setInitiate({
                url,
                method,
                body: { tierId: plan.id, addons: [] },
            });
            setPaymentStarted(true);
        }
    };

    // Free plan only - no Stripe involved, just the existing instant-assign
    // endpoint (subscription-guard-service.js's setEntitySubscription).
    const handleConfirmFree = () => {
        if (!selectedPlan || !user?.entityId) return;
        setProcessing(true);
        const { url, method } = buildApiRequestConfig(routes.subscription.select, [
            { param: 'entityId', value: user.entityId }
        ]);
        setSelect({ url, method, body: { tierId: selectedPlan.id } });
    };

    React.useEffect(() => {
        if (selectResult.data && selectedPlan) {
            setProcessing(false);
            // A paying company moving to Free: scheduled for the end of the
            // period already paid for, not switched now.
            const scheduled = selectResult.data as { scheduled?: boolean; endsOn?: string | null };
            if (scheduled.scheduled) {
                const when = scheduled.endsOn ? new Date(scheduled.endsOn).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'the end of your current period';
                showSuccess(`Your current plan stays active until ${when}, then moves to ${selectedPlan.name}. Auto-renew has been turned off.`);
                onSelect?.(selectedPlan);
                onClose();
                return;
            }
            setReceipt(buildReceipt(selectedPlan, null));
            setStep('success');
            onSelect?.(selectedPlan);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectResult.data]);

    // Fired by PaidCheckoutStep once Stripe confirms the charge succeeded -
    // the entity's tier itself is promoted by the invoice.paid webhook
    // (subscription-webhook-service.js), not by this client, so this is
    // purely "show the success screen and tell whoever opened me to refresh".
    const handlePaidSuccess = () => {
        if (!selectedPlan) return;
        setReceipt(buildReceipt(selectedPlan, null, initiateResult.data?.suggestedRefund));
        setStep('success');
        // Apply the payment now rather than waiting on Stripe's webhook (the
        // company stayed on Free when that never arrived), then refresh.
        const plan = selectedPlan;
        if (user?.entityId) {
            syncSubscriptionAfterPayment(user.entityId).finally(() => onSelect?.(plan));
        } else {
            onSelect?.(plan);
        }
    };

    // mode: 'change' with no clientSecret (alreadyCharged) means
    // changePlan already billed the entity's existing saved payment method
    // in full - there's no PaymentElement/confirm step to wait for, so this
    // goes straight to success the moment that result comes back.
    React.useEffect(() => {
        if (alreadyCharged) handlePaidSuccess();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alreadyCharged]);

    // One card per visible plan, in the admin's display order (Admin >
    // Subscription Tiers): Free has no Monthly/Yearly versions, every other
    // plan shows its version for the chosen billing cycle.
    const allTiers = tiers.length ? tiers : DEFAULT_TIERS;
    const orderedPlans = allTiers
        .filter(t => t.isVisible !== false && (!t.billing || t.billing === billing || (t.billing === 'daily' && billing === 'monthly')))
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.pricePerMonth - b.pricePerMonth);
    // The "Most popular" plan is the focus: always placed in the middle of
    // the lineup (the rest keep their display order around it), and the
    // carousel opens centred on it - whatever plans are added, hidden or
    // deleted. With the defaults that's Free, Elite, Pro.
    const featuredPlan = orderedPlans.find(t => t.highlight);
    const visiblePlans = featuredPlan
        ? (() => {
            const rest = orderedPlans.filter(t => t !== featuredPlan);
            const middle = Math.floor(orderedPlans.length / 2);
            return [...rest.slice(0, middle), featuredPlan, ...rest.slice(middle)];
        })()
        : orderedPlans;
    const focusIndex = featuredPlan ? visiblePlans.indexOf(featuredPlan) : 0;
    // On Yearly, a plan's price is compared against its own Monthly version.
    const monthlyVersionOf = (plan: SubscriptionPlan) => (
        plan.billing === 'annual' ? allTiers.find(t => t.id === `${planKeyOf(plan.id)}-monthly`) : undefined
    );
    const comparePriceFor = (plan: SubscriptionPlan) => monthlyVersionOf(plan)?.pricePerMonth;
    const savingsFor = (plan: SubscriptionPlan) => {
        const monthly = monthlyVersionOf(plan);
        return monthly ? Math.max(0, monthly.pricePerMonth - plan.pricePerMonth) : 0;
    };

    // Upgrade mode ranks plans by forecast horizon - the same measure the
    // horizon gates upgrade on (upgrade-required-dialog.tsx).
    const upgradeFrom = upgradeFromTierId ? tiers.find(t => t.id === upgradeFromTierId) : undefined;
    const unavailableLabelFor = (plan: SubscriptionPlan): string | undefined => {
        if (currentTierId && plan.id === currentTierId) return 'Current plan';
        if (!upgradeFrom) return undefined;
        if (plan.id === upgradeFrom.id) return 'Current plan';
        return plan.horizonDays <= upgradeFrom.horizonDays ? 'Not an upgrade' : undefined;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen
            TransitionComponent={SlideUpTransition}
            PaperProps={{ sx: { backgroundColor: 'var(--color-page-background)' } }}
        >
            {/* Page-style chrome (header bar + scrollable body) rather than the
                usual centered dialog box - this is meant to be spawnable from
                anywhere in the app and read as a full page while it's open,
                not a popup layered on top of one. */}
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: isMobile ? 2 : 4,
                        py: 2,
                        flexShrink: 0,
                    }}
                >
                    <img src={isDark ? logoDarkImage : logoImage} alt="FineAnts" style={{ width: 128, height: 32 }} />
                    <IconButton onClick={onClose} size="small" sx={{ backgroundColor: 'var(--color-container-background)' }}>
                        <X size={20} />
                    </IconButton>
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ maxWidth: 980, mx: 'auto', px: isMobile ? 2 : 4, pb: 6, width: '100%' }}>
                    {step === 'checkout' && selectedPlan && selectedPlan.pricePerMonth > 0 ? (
                        stripeClientSecret && stripePromise ? (
                            <Elements stripe={stripePromise} options={{ clientSecret: stripeClientSecret, appearance: { theme: 'stripe' } }}>
                                <PaidCheckoutStep
                                    plan={selectedPlan}
                                    billing={selectedPlan.billing === 'daily' ? 'daily' : billing}
                                    onBack={() => setStep('plans')}
                                    onPaid={handlePaidSuccess}
                                    addonLines={addonLinesFor(selectedPlan)}
                                    addonOptions={addonOptionsFor(selectedPlan)}
                                    selectedAddonKeys={checkoutAddons}
                                    onChangeAddons={() => { setPaymentStarted(false); setInitiate(null); }}
                                />
                            </Elements>
                        ) : (
                            <CheckoutView
                                plan={selectedPlan}
                                billing={selectedPlan.billing === 'daily' ? 'daily' : billing}
                                processing={false}
                                billingFieldsSlot={null}
                                errorMessage={stripeInitError || stripeKeyError}
                                addonLines={addonLinesFor(selectedPlan)}
                                addonOptions={addonOptionsFor(selectedPlan)}
                                selectedAddonKeys={checkoutAddons}
                                onToggleAddon={toggleCheckoutAddon}
                                addonsLocked={paymentStarted}
                                onContinueToPayment={paymentStarted ? undefined : () => startPayment(selectedPlan, addonsFor(selectedPlan))}
                                onBack={() => setStep('plans')}
                                onConfirm={() => {}}
                            />
                        )
                    ) : step === 'checkout' && selectedPlan ? (
                        <CheckoutView
                            plan={selectedPlan}
                            billing={selectedPlan.billing === 'daily' ? 'daily' : billing}
                            processing={processing}
                            onBack={() => setStep('plans')}
                            onConfirm={handleConfirmFree}
                        />
                    ) : step === 'success' && receipt ? (
                        <SuccessView receipt={receipt} onContinue={() => setStep('access')} />
                    ) : step === 'access' && selectedPlan ? (
                        <AccessView plan={selectedPlan} onGoToDashboard={onClose} />
                    ) : (
                    <>
                        <Box sx={{ textAlign: 'center', mb: 4 }}>
                            <Typography sx={{ fontSize: isMobile ? 24 : 32, fontWeight: 800, letterSpacing: -0.5 }}>
                                {upgradeFrom ? 'Upgrade your plan' : currentTierId ? 'Manage your plan' : 'Choose your plan'}
                            </Typography>
                            <Typography sx={{ fontSize: 14, color: 'var(--color-text-muted)', mt: 1, maxWidth: 480, mx: 'auto' }}>
                                Every plan includes the full Core feature set. Pay yearly up front to lock in a lower monthly rate.
                            </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, mb: 5 }}>
                            {/* Matches the toggle's own width below (two equal halves) so this
                                sits directly above the Yearly half specifically, not centered
                                over the whole pill. */}
                            <Box sx={{ display: 'flex', width: 320 }}>
                                <Box sx={{ flex: 1 }} />
                                <Typography sx={{ flex: 1, textAlign: 'center', fontSize: 12.5, fontWeight: 700, color: 'var(--color-primary)' }}>
                                    Save up to 25%
                                </Typography>
                            </Box>
                            <ToggleButtonGroup
                                value={billing}
                                exclusive
                                onChange={(_e, value) => value && setBilling(value)}
                                sx={{
                                    width: 320,
                                    backgroundColor: 'var(--color-container-background)',
                                    borderRadius: 999,
                                    p: 0.5,
                                    '& .MuiToggleButton-root': {
                                        flex: 1,
                                        textTransform: 'uppercase',
                                        letterSpacing: 0.3,
                                        fontWeight: 700,
                                        fontSize: 13.5,
                                        px: 3,
                                        py: 1,
                                        border: 'none',
                                        borderRadius: 999,
                                        color: 'var(--color-text-muted)',
                                        '&.Mui-selected': {
                                            backgroundColor: 'var(--color-primary)',
                                            color: '#fff',
                                            '&:hover': { backgroundColor: 'var(--color-primary)' },
                                        },
                                    },
                                }}
                            >
                                <ToggleButton value="monthly" disableRipple>Monthly</ToggleButton>
                                <ToggleButton value="annual" disableRipple>Yearly</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        {/* Display order comes from Admin > Subscription Tiers - the
                            default puts the featured plan in the middle (Free, Elite,
                            Pro), where eyes land first. Each card keeps its per-plan
                            breakdown underneath (only what that plan actually has,
                            never a greyed-out excluded item, matching the reference
                            page) and they scroll together. */}
                        <PlanCarousel
                            isMobile={isMobile}
                            focusIndex={focusIndex}
                            focusKey={billing}
                            items={visiblePlans.map(plan => ({
                                key: plan.id,
                                card: (
                                    <PlanCard
                                        plan={plan}
                                        comparePrice={comparePriceFor(plan)}
                                        savingsPerMonth={savingsFor(plan)}
                                        dark={plan.pricePerMonth > 0 && !plan.highlight}
                                        unavailableLabel={unavailableLabelFor(plan)}
                                        onSelect={handlePickPlan}
                                        />
                                ),
                                features: <PlanFeatureColumn plan={plan} />,
                            }))}
                        />

                        <Box sx={{ textAlign: 'center', mt: 5 }}>
                            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>* Prices in AUD</Typography>
                            <Typography sx={{ fontSize: 12, color: 'var(--color-text-muted)' }}>Offer subject to terms of use.</Typography>
                        </Box>
                    </>
                    )}
                    </Box>
                    {step === 'plans' && <PlansFooter isDark={isDark} />}
                </Box>
            </Box>
        </Dialog>
    );
};

// ─── Add-ons checkout ─────────────────────────────────────────────────────────
// Company Settings > Subscription's add-ons, as the same checkout page a plan
// is bought on (CheckoutView). Ticked = on. Ticking one charges its full price
// for the current period now (to the card the subscription bills); unticking
// one turns it off at the end of the period already paid for
// (express-server subscription-addons-service.js).

// One add-on as Company Settings gets it (getAddonsState).
export type AddonState = {
    key: string;
    label: string;
    price: number | null;
    chargePerPeriod: number;
    billing: 'monthly' | 'annual' | 'daily' | null;
    enabled: boolean;
    endsAt: string | null;
    hasAccess: boolean;
    billedFrom: string | null;
};

export const AddonsCheckout: React.FC<{
    open: boolean;
    onClose: () => void;
    plan: SubscriptionPlan | null;
    addons: AddonState[];
    readOnly?: boolean;
    // Ticked on open on top of what's on - e.g. the horizon add-on a prompt suggested.
    preselect?: string[];
    // After the changes are applied (or some failed) - refresh the subscription.
    onDone: () => void;
    // Only when every change went through.
    onApplied?: () => void;
}> = ({ open, onClose, plan, addons, readOnly = false, preselect = [], onDone, onApplied }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { isDark } = useColorScheme();
    const { user } = useUser();
    const { companySettings } = useSettings();
    const { showSuccess } = useToast();
    const [selected, setSelected] = React.useState<string[]>([]);
    const [processing, setProcessing] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState('');

    React.useEffect(() => {
        if (!open) return;
        setSelected(Array.from(new Set([...addons.filter(a => a.enabled).map(a => a.key), ...preselect.filter(k => addons.some(a => a.key === k))])));
        setErrorMessage('');
        setProcessing(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const billing: BillingCycle = plan?.billing === 'annual' ? 'annual' : plan?.billing === 'daily' ? 'daily' : 'monthly';
    const toTurnOn = addons.filter(a => !a.enabled && selected.includes(a.key));
    const toTurnOff = addons.filter(a => a.enabled && !selected.includes(a.key));
    // Turned off but still inside the paid period - turning it back on isn't charged again.
    const charged = toTurnOn.filter(a => !a.hasAccess && a.chargePerPeriod > 0);
    const dueNow = charged.reduce((sum, a) => sum + a.chargePerPeriod, 0);
    const hasChanges = toTurnOn.length > 0 || toTurnOff.length > 0;

    const toggle = (key: string, checked: boolean) => setSelected(prev => (checked ? [...prev.filter(k => k !== key), key] : prev.filter(k => k !== key)));

    const apply = async () => {
        if (!user?.entityId || !hasChanges) return;
        setProcessing(true);
        setErrorMessage('');
        const token = Cookies.get('token');
        // What the server actually charged - not what was expected.
        let charged = 0;
        try {
            // On first (each one charged), then off.
            for (const [addon, enabled] of [...toTurnOn.map(a => [a, true] as const), ...toTurnOff.map(a => [a, false] as const)]) {
                const { url, method } = buildApiRequestConfig(routes.subscription.setAddon, [
                    { param: 'entityId', value: user.entityId },
                    { param: 'addonKey', value: addon.key },
                ]);
                const { data } = await axios({ url, method, data: { enabled }, headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) } });
                charged += Number(data?.charged) || 0;
            }
            onDone();
            showSuccess(charged > 0 ? `Paid ${formatMoney(charged)} - add-ons updated` : 'Add-ons updated');
            onApplied?.();
            onClose();
        } catch (err: any) {
            setErrorMessage(err?.response?.data?.error || err?.message || 'Failed to update add-ons');
            onDone();
        } finally {
            setProcessing(false);
        }
    };

    if (!plan) return null;

    return (
        <Dialog
            open={open}
            onClose={processing ? undefined : onClose}
            fullScreen
            TransitionComponent={SlideUpTransition}
            PaperProps={{ sx: { backgroundColor: 'var(--color-page-background)' } }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: isMobile ? 2 : 4, py: 2, flexShrink: 0 }}>
                    <img src={isDark ? logoDarkImage : logoImage} alt="FineAnts" style={{ width: 128, height: 32 }} />
                    <IconButton onClick={onClose} disabled={processing} size="small" sx={{ backgroundColor: 'var(--color-container-background)' }}>
                        <X size={20} />
                    </IconButton>
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', px: isMobile ? 2 : 4, pb: 4 }}>
                    <Box sx={{ pt: 1, maxWidth: 820, mx: 'auto' }}>
                        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 4 : 5, alignItems: 'flex-start' }}>
                            {/* LEFT */}
                            <Box sx={{ flex: '1 1 0', minWidth: 0, width: '100%', order: isMobile ? 2 : 1 }}>
                                <KongSectionHeader icon={<User size={15} />}>Your Details</KongSectionHeader>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                                    <CheckoutDetailRow label="Full name" value={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()} />
                                    <CheckoutDetailRow label="Email address" value={user?.email} />
                                    <CheckoutDetailRow label="Company" value={companySettings?.company?.name} />
                                </Box>

                                <KongSectionHeader icon={<Plus size={15} />}>Add-ons</KongSectionHeader>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {addons.map(addon => {
                                        const checked = selected.includes(addon.key);
                                        const locked = readOnly || processing;
                                        return (
                                            <Box
                                                key={addon.key}
                                                component="label"
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.25,
                                                    p: 1.25,
                                                    borderRadius: 2,
                                                    border: '1px solid',
                                                    borderColor: checked ? 'var(--color-primary)' : 'rgba(0,0,0,0.12)',
                                                    background: checked ? 'rgba(var(--color-primary-rgb), 0.06)' : 'transparent',
                                                    cursor: locked ? 'default' : 'pointer',
                                                }}
                                            >
                                                <Checkbox
                                                    size="small"
                                                    checked={checked}
                                                    disabled={locked}
                                                    onChange={(e) => toggle(addon.key, e.target.checked)}
                                                    sx={{ p: 0, '&.Mui-checked': { color: 'var(--color-primary)' } }}
                                                />
                                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                                    <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{addon.label}</Typography>
                                                    <Typography sx={{ fontSize: 11.5, color: 'var(--color-text-muted)' }}>{addonDescription(addon.key)}</Typography>
                                                </Box>
                                                <Typography sx={{ fontSize: 14, fontWeight: 800, color: 'var(--color-primary)', whiteSpace: 'nowrap' }}>
                                                    {addon.chargePerPeriod > 0 ? `+${formatMoney(addon.chargePerPeriod)}` : 'Free'}
                                                    {addon.chargePerPeriod > 0 && (
                                                        <Box component="span" sx={{ fontSize: 11, fontWeight: 500, opacity: 0.8 }}>
                                                            {billing === 'annual' ? ' / 12 months' : billing === 'daily' ? '/day' : '/mo'}
                                                        </Box>
                                                    )}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>

                                <KongSectionHeader icon={<CreditCard size={15} />}>Billing Information</KongSectionHeader>
                                <Typography sx={{ fontSize: 14 }}>Paid via card on file</Typography>
                                {errorMessage && (
                                    <Typography sx={{ fontSize: 12.5, color: '#e53935', mt: 1.5 }}>{errorMessage}</Typography>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Lock size={12} color="var(--color-text-muted)" />
                                        <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>SSL Encryption</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <ShieldCheck size={12} color="var(--color-text-muted)" />
                                        <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Privacy Protected</Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* RIGHT */}
                            <Box sx={{ width: isMobile ? '100%' : 340, flexShrink: 0, order: isMobile ? 1 : 2, position: isMobile ? 'static' : 'sticky', top: 16 }}>
                                <Typography sx={{ fontSize: 20, fontWeight: 800 }}>{plan.name} Plan</Typography>
                                <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.4, mt: 1, mb: 3 }}>Add-ons</Typography>

                                <Box sx={{ borderRadius: 3, border: '1px solid rgba(0,0,0,0.12)', p: 2.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                                        <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>{formatMoney(dueNow)}</Typography>
                                        <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>due now</Typography>
                                    </Box>
                                    {(charged.length > 0 || toTurnOff.length > 0) && (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, mt: 1.5, fontSize: 12.5 }}>
                                            {charged.map(addon => (
                                                <Box key={addon.key} sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: 'var(--color-primary)' }}>
                                                    <span>+ {addon.label}</span>
                                                    <span>{formatMoney(addon.chargePerPeriod)}</span>
                                                </Box>
                                            ))}
                                            {toTurnOff.map(addon => (
                                                <Box key={addon.key} sx={{ display: 'flex', justifyContent: 'space-between', color: 'var(--color-text-muted)' }}>
                                                    <span>- {addon.label}</span>
                                                    <span>Off at renewal</span>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}

                                    {!readOnly && (
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            disabled={processing || !hasChanges}
                                            startIcon={processing ? undefined : <ShoppingCart size={16} />}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                fontSize: 15,
                                                borderRadius: 999,
                                                py: 1.3,
                                                mt: 2,
                                                boxShadow: 'none',
                                                backgroundColor: 'var(--color-primary)',
                                                color: '#fff',
                                                '&:hover': { backgroundColor: 'var(--color-primary)', opacity: 0.92, boxShadow: 'none' },
                                            }}
                                            onClick={apply}
                                        >
                                            {processing ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : dueNow > 0 ? `Pay ${formatMoney(dueNow)} now` : 'Save changes'}
                                        </Button>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
};

// ─── Overdue payment ──────────────────────────────────────────────────────────
// A subscription payment that didn't go through (a failed renewal, or a plan
// change that was never paid) - paid here with a card, on the same checkout
// page a plan is bought on. Paying also makes that card the subscription's
// card for future renewals and add-ons.

export type OverduePayment = { overdue: boolean; amount?: number; planName?: string | null; billing?: 'monthly' | 'annual' | 'daily' | null };

const OverduePayStep: React.FC<{ amount: number; onPaid: () => void }> = ({ amount, onPaid }) => {
    const { confirm, processing, error } = useConfirmStripePayment(() => onPaid());
    return (
        <>
            <PaymentElement />
            {error && <Typography sx={{ fontSize: 12.5, color: '#e53935', mt: 1.5 }}>{error}</Typography>}
            <Button
                variant="contained"
                fullWidth
                disabled={processing}
                startIcon={processing ? undefined : <ShoppingCart size={16} />}
                sx={{
                    textTransform: 'none',
                    fontWeight: 700,
                    fontSize: 15,
                    borderRadius: 999,
                    py: 1.3,
                    mt: 2.5,
                    boxShadow: 'none',
                    backgroundColor: 'var(--color-primary)',
                    color: '#fff',
                    '&:hover': { backgroundColor: 'var(--color-primary)', opacity: 0.92, boxShadow: 'none' },
                }}
                onClick={confirm}
            >
                {processing ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : `Pay ${formatMoney(amount)} now`}
            </Button>
        </>
    );
};

export const OverduePaymentCheckout: React.FC<{
    open: boolean;
    onClose: () => void;
    overdue: OverduePayment | null;
    // After the payment - the subscription is synced from Stripe first.
    onPaid: () => void;
}> = ({ open, onClose, overdue, onPaid }) => {
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { isDark } = useColorScheme();
    const { user } = useUser();
    const { companySettings } = useSettings();
    const { showSuccess } = useToast();
    const { stripePromise, error: stripeKeyError } = usePlatformStripe();
    const [start, setStart] = useApi<{ success: boolean; clientSecret: string; amount: number }>();
    const [finishing, setFinishing] = React.useState(false);

    React.useEffect(() => {
        if (!open || !user?.entityId) return;
        setFinishing(false);
        setStart(buildApiRequestConfig(routes.subscription.payOverdue, [{ param: 'entityId', value: user.entityId }]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const clientSecret = start.data?.clientSecret ?? null;
    const amount = start.data?.amount ?? overdue?.amount ?? 0;
    const startError = start.error ? (start.errorData?.error || (typeof start.error === 'string' ? start.error : 'Failed to start the payment.')) : '';

    const handlePaid = async () => {
        if (!user?.entityId) return;
        setFinishing(true);
        await syncSubscriptionAfterPayment(user.entityId);
        showSuccess(`Paid ${formatMoney(amount)}`);
        onPaid();
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={finishing ? undefined : onClose}
            fullScreen
            TransitionComponent={SlideUpTransition}
            PaperProps={{ sx: { backgroundColor: 'var(--color-page-background)' } }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: isMobile ? 2 : 4, py: 2, flexShrink: 0 }}>
                    <img src={isDark ? logoDarkImage : logoImage} alt="FineAnts" style={{ width: 128, height: 32 }} />
                    <IconButton onClick={onClose} disabled={finishing} size="small" sx={{ backgroundColor: 'var(--color-container-background)' }}>
                        <X size={20} />
                    </IconButton>
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', px: isMobile ? 2 : 4, pb: 4 }}>
                    <Box sx={{ pt: 1, maxWidth: 820, mx: 'auto' }}>
                        <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 4 : 5, alignItems: 'flex-start' }}>
                            {/* LEFT */}
                            <Box sx={{ flex: '1 1 0', minWidth: 0, width: '100%', order: isMobile ? 2 : 1 }}>
                                <KongSectionHeader icon={<User size={15} />}>Your Details</KongSectionHeader>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
                                    <CheckoutDetailRow label="Full name" value={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim()} />
                                    <CheckoutDetailRow label="Email address" value={user?.email} />
                                    <CheckoutDetailRow label="Company" value={companySettings?.company?.name} />
                                </Box>

                                <KongSectionHeader icon={<CreditCard size={15} />}>Billing Information</KongSectionHeader>
                                {startError || stripeKeyError ? (
                                    <Typography sx={{ fontSize: 12.5, color: '#e53935' }}>{startError || stripeKeyError}</Typography>
                                ) : finishing ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
                                        <CircularProgress size={18} sx={{ color: 'var(--color-primary)' }} />
                                        <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Finishing up…</Typography>
                                    </Box>
                                ) : clientSecret && stripePromise ? (
                                    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
                                        <OverduePayStep amount={amount} onPaid={handlePaid} />
                                    </Elements>
                                ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
                                        <CircularProgress size={18} sx={{ color: 'var(--color-primary)' }} />
                                        <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>Preparing secure checkout…</Typography>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Lock size={12} color="var(--color-text-muted)" />
                                        <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>SSL Encryption</Typography>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <ShieldCheck size={12} color="var(--color-text-muted)" />
                                        <Typography sx={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Privacy Protected</Typography>
                                    </Box>
                                </Box>
                            </Box>

                            {/* RIGHT */}
                            <Box sx={{ width: isMobile ? '100%' : 340, flexShrink: 0, order: isMobile ? 1 : 2, position: isMobile ? 'static' : 'sticky', top: 16 }}>
                                <Typography sx={{ fontSize: 20, fontWeight: 800 }}>{overdue?.planName ? `${overdue.planName} Plan` : 'Subscription'}</Typography>
                                <Typography sx={{ fontSize: 15, fontWeight: 700, lineHeight: 1.4, mt: 1, mb: 3 }}>Overdue payment</Typography>
                                <Box sx={{ borderRadius: 3, border: '1px solid rgba(0,0,0,0.12)', p: 2.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                                        <Typography sx={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>{formatMoney(amount)}</Typography>
                                        <Typography sx={{ fontSize: 13, color: 'var(--color-text-muted)' }}>due now</Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Dialog>
    );
};

export default SubscriptionPlans;
